import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import { cardSx, colour } from './tokens';

/**
 * The numbers at the top of the dashboard, and the small chart beside them.
 *
 * Drawn with plain boxes rather than a charting library: the shapes here are a row of counts and
 * a column of daily totals, which is a handful of divs. Adding recharts or x-charts to the
 * bundle for that would cost more to review, install and carry than it saves.
 */

/**
 * One number, with a word for what it counts. Clickable when it filters the list below.
 *
 * `size="lead"` is for the handful of figures somebody opens this page to see. Everything used to
 * be one size, which made nine tiles in an undifferentiated row — how many children are coming
 * back, how much money has settled, and how far behind the TASS bot is, all shouting equally, so
 * none of them read as the answer to anything.
 *
 * The width is capped. Tiles grow to fill their row, and on the last row of a wrap that left two
 * tiles stretched half the page wide each, which read as a different and more important kind of
 * thing rather than as the leftovers of a row.
 */
export const StatTile = ({ label, value, tone = 'neutral', hint, onClick, active = false, size = 'normal' }) => {
    const tones = {
        neutral: { value: colour.ink, accent: colour.borderStrong },
        good: { value: colour.primaryDeep, accent: colour.primary },
        warn: { value: colour.warnInk, accent: colour.warnBorder },
        info: { value: colour.infoInk, accent: '#2563EB' },
    };
    const tone_ = tones[tone] || tones.neutral;
    const lead = size === 'lead';

    return (
        <Box
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined}
            sx={{
                ...cardSx,
                flex: lead ? '1 1 210px' : '1 1 165px',
                minWidth: 0,
                maxWidth: { xs: '100%', sm: lead ? 340 : 280 },
                p: { xs: 1.5, sm: lead ? 2.5 : 2 },
                position: 'relative',
                cursor: onClick ? 'pointer' : 'default',
                borderColor: active ? colour.primary : colour.border,
                boxShadow: active
                    ? '0 4px 14px rgba(5, 150, 105, 0.18)'
                    : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': onClick ? { borderColor: colour.primary } : {},
            }}
        >
            {/* A short bar in the tile's own colour, so the tiles are told apart at a glance. */}
            <Box sx={{
                width: lead ? 34 : 28,
                height: lead ? 4 : 3,
                bgcolor: tone_.accent,
                borderRadius: 2,
                mb: lead ? 1.5 : 1.25,
            }} />
            <Typography sx={{
                fontSize: lead
                    ? { xs: '1.9rem', sm: '2.6rem' }
                    : { xs: '1.35rem', sm: '1.55rem' },
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                color: tone_.value,
            }}>
                {value}
            </Typography>
            <Typography sx={{
                fontSize: lead ? '14px' : '12.5px',
                color: lead ? colour.body : colour.muted,
                fontWeight: lead ? 700 : 600,
                mt: 0.4,
            }}>
                {label}
            </Typography>
            {hint && (
                <Typography sx={{ fontSize: lead ? '12px' : '11px', color: colour.faint, mt: 0.5 }}>
                    {hint}
                </Typography>
            )}
        </Box>
    );
};

/**
 * A named row of tiles.
 *
 * The heading is the point: without one, a reader has to work out from the labels alone that the
 * first three tiles count children and the next four count money. Tiles wrap within their own
 * group, so a group never bleeds into the one below it.
 */
export const StatGroup = ({ title, children, sx = {} }) => (
    <Box sx={{ mb: 2.5, ...sx }}>
        {title && (
            <Typography sx={{
                fontSize: '11.5px',
                fontWeight: 700,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: colour.faint,
                mb: 1,
                px: { xs: 2, sm: 0 },
            }}>
                {title}
            </Typography>
        )}
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'stretch' }}>
            {children}
        </Box>
    </Box>
);

/**
 * Re-enrolments per day.
 *
 * A column per day, tallest day full height. Days with nothing still get a sliver, so a gap in
 * the run reads as "none that day" rather than as the chart having stopped.
 */
export const DailyChart = ({ days = [], height = 120 }) => {
    const most = Math.max(1, ...days.map((day) => day.value));

    if (!days.length) {
        return (
            <Typography sx={{ fontSize: '13px', color: colour.faint, py: 4, textAlign: 'center' }}>
                Nothing submitted yet.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 0.5, sm: 0.75 }, height, px: 0.5 }}>
            {days.map((day) => (
                <Tooltip key={day.label} title={`${day.value} on ${day.label}`} arrow>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                        <Box sx={{
                            height: `${Math.max(3, Math.round((day.value / most) * 100))}%`,
                            bgcolor: day.value ? colour.primary : colour.border,
                            opacity: day.value ? 0.85 : 1,
                            borderRadius: '4px 4px 2px 2px',
                            transition: 'height 0.2s ease',
                            '&:hover': { opacity: 1 },
                        }} />
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
};

/** The day labels under the chart — only a few, so they stay readable on a phone. */
export const DailyChartAxis = ({ days = [] }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography sx={{ fontSize: '11px', color: colour.faint }}>{days[0]?.label}</Typography>
        <Typography sx={{ fontSize: '11px', color: colour.faint }}>{days[days.length - 1]?.label}</Typography>
    </Box>
);

/**
 * How the campuses compare, as a row of proportional bars.
 *
 * Useful to somebody who holds more than one campus — which is the whole reason the campus
 * filter exists — and meaningless to somebody who holds one, so the dashboard leaves it out
 * for them rather than drawing a single full-width bar.
 */
export const CampusBars = ({ campuses = [] }) => {
    const most = Math.max(1, ...campuses.map((campus) => campus.total));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {campuses.map((campus) => (
                <Box key={campus.campus}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: colour.body }}>
                            {campus.campus}
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: colour.muted }}>
                            {campus.pending
                                ? `${campus.pending} waiting of ${campus.total}`
                                : `${campus.total}`}
                        </Typography>
                    </Box>
                    <Box sx={{ position: 'relative', height: 8, bgcolor: colour.panel, borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            width: `${Math.round((campus.total / most) * 100)}%`,
                            bgcolor: colour.primaryTintBorder,
                        }} />
                        {/* The part still waiting, drawn over the total. */}
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            width: `${Math.round((campus.pending / most) * 100)}%`,
                            bgcolor: colour.primary,
                        }} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};
